<script setup>
import { computed, ref, toRefs } from 'vue';
import imageCompression from 'browser-image-compression';

import FloatLabel from 'primevue/floatlabel';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import { useToast } from 'primevue/usetoast';
import ProgressBar from 'primevue/progressbar';

import {
  faRegularImageCirclePlus,
  faTagShareit,
} from '@awesome.me/kit-a54fb3a664/icons/kit/custom';

import ButtonElement from '@/components/ButtonElement.vue';
import TextElement from './TextElement.vue';

import { getUploadUrl, uploadFile, submitShareit } from '@/services/api';

const toast = useToast();

const props = defineProps(['settings']);
const { settings } = toRefs(props);

const attributionText = ref('');
const attributionTextMaxLength = ref(50);
const commentText = ref('');
const commentTextMaxLength = ref(250);

const showProgressBar = ref(false);
const fileUploaded = ref(false);
const tagboardId = computed(() => settings.value.tagboardId);

const tosFontFamily = computed(() => `'${settings?.value?.tos.fontFamily}'` ?? 'Barlow');
const tosTextColor = computed(() => settings?.value?.tos.textColor ?? 'Barlow');
const tosLinkColor = computed(() => settings?.value?.tos.linkColor ?? 'var(--link-color)');
const requiredText = computed(() => settings?.value?.requiredFieldsText?.text ?? '* Required');
const requiredTextColor = computed(() => settings?.value?.requiredFieldsText?.textColor ?? 'var(--text-color)');
const requiredTextSize = computed(() => `${settings?.value?.requiredFieldsText?.textSize ?? '10'}px`);
const requiredTextFont = computed(() => (settings?.value?.requiredFieldsText?.font ? `'${settings?.value?.requiredFieldsText?.font}'` : 'Barlow'));

const allowMediaUpload = computed(() => settings.value.allowMediaUpload ?? true);
const mediaUploadRequired = computed(() => settings.value.mediaUploadRequired ?? true);
const allowAttribution = computed(() => settings.value.allowAttribution ?? false);
const attributionRequired = computed(() => settings.value.attributionRequired ?? false);
const allowComment = computed(() => settings.value.allowComment ?? false);
const commentRequired = computed(() => settings.value.commentRequired ?? false);

const collectText = computed(() => allowAttribution.value || allowComment.value);

// There are actually three buttons coding all with same style for now,
// hard coding text in all but first one.
const fileSelectButtonSettings = computed(() => ({
  text: settings.value.fileSelectButton.text,
  icon: faRegularImageCirclePlus, // hard coded for now
  backgroundColor: settings.value.fileSelectButton.backgroundColor,
  textColor: settings.value.fileSelectButton.textColor,
  borderRadius: settings.value.fileSelectButton.borderRadius,
  textSize: settings.value.fileSelectButton.textSize,
  font: settings.value.fileSelectButton.font,
}));

const shareItButtonSettings = computed(() => ({
  text: 'Share It!',
  icon: faTagShareit, // hard coded for now
  backgroundColor: settings.value.fileSelectButton.backgroundColor,
  textColor: settings.value.fileSelectButton.textColor,
  borderRadius: settings.value.fileSelectButton.borderRadius,
  textSize: settings.value.fileSelectButton.textSize,
  font: settings.value.fileSelectButton.font,
}));

const cancelButtonSettings = computed(() => ({
  text: 'Try Another',
  backgroundColor: settings.value.fileSelectButton.backgroundColor,
  textColor: settings.value.fileSelectButton.textColor,
  borderRadius: settings.value.fileSelectButton.borderRadius,
  textSize: settings.value.fileSelectButton.textSize,
  font: settings.value.fileSelectButton.font,
}));

const uploadAgainButtonSettings = computed(() => ({
  text: 'Submit Another',
  backgroundColor: settings.value.fileSelectButton.backgroundColor,
  textColor: settings.value.fileSelectButton.textColor,
  borderRadius: settings.value.fileSelectButton.borderRadius,
  textSize: settings.value.fileSelectButton.textSize,
  font: settings.value.fileSelectButton.font,
}));

const compressImage = async (file) => {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };
  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error(error);
    throw new Error('Image compression failed');
  }
};

const uploadAndCompress = async (files, callback) => {
  try {
    showProgressBar.value = true;
    let fKey = '';

    if (!attributionText.value && !commentText.value && files.length === 0) {
      throw new Error('Whoops! You need to fill in at least one field.');
    }

    if (allowAttribution.value && attributionRequired.value) {
      if (!attributionText.value) {
        throw new Error('Attribution is required');
      }
    }

    if (allowComment.value && commentRequired.value) {
      if (!commentText.value) {
        throw new Error('Comment is required');
      }
    }

    if (allowMediaUpload.value) {
      // If media upload is required, check if files are provided
      if (mediaUploadRequired.value && (!files || files.length === 0)) {
        throw new Error('Media upload is required');
      }

      const file = files[0];
      if (file) {
        // Get Pre-Signed URL
        const { putUrl, fileKey } = await getUploadUrl(file, tagboardId.value);

        fKey = fileKey;
        // Compress File
        const compressedFile = await compressImage(file);

        // Upload File
        await uploadFile(putUrl, compressedFile);
      }
    }

    await submitShareit({
      tagboardId: tagboardId.value,
      fileKey: fKey,
      attribution: attributionText.value,
      comment: commentText.value,
    });

    showProgressBar.value = false;

    callback();
    toast.add({
      severity: 'info',
      summary: 'Success',
      detail: 'Your content has been submitted.',
      life: 10000,
    });

    fileUploaded.value = true;
  } catch (error) {
    showProgressBar.value = false;
    console.error(error);
    toast.add({
      severity: 'error',
      summary: `${error.message || error}`,
      detail: 'Please try again.',
      life: 10000,
    });
  }
};

const resetUpload = () => {
  fileUploaded.value = false;
  attributionText.value = '';
  commentText.value = '';
};
</script>
<template>
  <div
    class="element share-it-element"
    data-entity="pages-shareShareItElement"
  >
    <FileUpload
      v-if="!fileUploaded"
      :pt:root:class="['share-it-element__file-upload file-upload']"
      name="file[]"
      accept="image/*"
      custom-upload
      @upload="uploadAndCompress"
      pt:root:style="display:flex; flex-direction:column-reverse"
      pt:content:class="file-upload__content"
      pt:buttonbar:class="file-upload__button-bar"
      data-entity="pages-shareShareItElement-fileUpload"
      data-test="pages-shareIt-element"
    >
      <template
        #header="{
          chooseCallback, clearCallback, files,
        }">
        <div class="flex items-center justify-center flex-col">

          <div
            v-if="allowMediaUpload && !files.length"
            class="flex items-center justify-center flex-col border-2 border-dashed rounded share-it-element__content"
            tabindex="0"
            role="button"
            @click="chooseCallback"
            @keydown.enter="chooseCallback"
          >
            <span class="headshot pi pi-user p-5 text-7xl text-gray-300 opacity-50 border-400 p-0 mb-1" />
            <p class="share-it-element__text share-it-element__share-text">
              <TextElement :settings="{ content: settings.shareText }" />
            </p>
            <p v-if="mediaUploadRequired" class="share-it-element__required-text">{{ requiredText }}</p>
          </div>

          <div v-if="allowComment" class="share-it-element__text-input py-1">
            <FloatLabel class="mt-5 w-full">
              <Textarea
                v-model="commentText"
                id="commentInput"
                class="w-full share-it-element__text-input"
                :disabled="showProgressBar"
                :maxlength="commentTextMaxLength"
                autocomplete="off"
                data-test="pages-shareIt-commentInput"
              />
              <label
                for="commentInput"
                class="share-it-element__text-label"
              >Comment</label>
            </FloatLabel>

            <div class="flex py-1">
              <p v-if="commentRequired" class="share-it-element__required-text">{{ requiredText }}</p>
              <div class="flex-1 text-right">
                <div class="text-right text-xs share-it-element__character-count" aria-hidden="true">{{ commentText.length }}/{{ commentTextMaxLength }}</div>
                <div class="sr-only" aria-live="polite">{{ commentTextMaxLength - commentText.length }} characters remaining.</div>
              </div>
            </div>
          </div>

          <div v-if="allowAttribution" class="share-it-element__text-input py-1">
            <FloatLabel class="mt-5 w-full">
              <InputText
                v-model="attributionText"
                id="attributionInput"
                class="w-full share-it-element__text-input"
                :disabled="showProgressBar"
                :maxlength="attributionTextMaxLength"
                autocomplete="off"
                data-test="pages-shareIt-attributionInput"
              />
              <label
                for="attributionInput"
                class="share-it-element__text-label"
              >Name</label>
            </FloatLabel>

            <div class="flex py-1">
              <p v-if="attributionRequired" class="share-it-element__required-text">{{ requiredText }}</p>
              <div class="flex-1 text-right">
                <div class="text-right text-xs share-it-element__character-count" aria-hidden="true">{{ attributionText.length }}/{{ attributionTextMaxLength }}</div>
                <div class="sr-only" aria-live="polite">{{ attributionTextMaxLength - attributionText.length }} characters remaining.</div>
              </div>
            </div>
          </div>

          <p v-if="collectText" class="share-it-element__text-disclaimer">Please do not include any personal or sensitive information in your response.</p>

          <ButtonElement
            v-if="allowMediaUpload && (!files || files.length === 0)"
            class="share-it-element__btn share-it-element__upload-btn"
            @click="uploadAndCompress(files, clearCallback)"
            :settings="fileSelectButtonSettings"
            data-entity="pages-shareShareItElement-upload"
            data-test="pages-shareIt-upload"
          />

          <template v-if="!showProgressBar">
            <div v-if="allowMediaUpload && files && files.length > 0" class="flex items-center justify-center flex-wrap">
              <ButtonElement
                class="share-it-element__btn share-it-element__share-btn"
                @click="uploadAndCompress(files, clearCallback)"
                :settings="shareItButtonSettings"
                data-entity="pages-shareShareItElement-share"
                data-test="pages-shareIt-submit"
              />
              <ButtonElement
                class="share-it-element__btn share-it-element__cancel-btn"
                @click="clearCallback"
                :settings="cancelButtonSettings"
                data-entity="pages-shareShareItElement-cancel"
                data-test="pages-shareIt-cancelButton"
              />
            </div>

            <!-- non-media share it -->
            <div v-else-if="!allowMediaUpload" class="flex items-center justify-center flex-wrap">
              <ButtonElement
                class="share-it-element__btn share-it-element__share-btn"
                @click="uploadAndCompress(files, clearCallback)"
                :settings="shareItButtonSettings"
                data-entity="pages-shareShareItElement-share"
                data-test="pages-shareIt-shareButton"
              />
            </div>
          </template>

        </div>
      </template>
      <template #content="contentProps">
        <div
          v-if="contentProps.files.length > 0"
          class="flex justify-center"
        >
          <div v-for="file in contentProps.files" :key="file.name + file.type + file.size" class="m-0 p-2 flex flex-column align-items-center border border-dashed rounded-lg">
            <div>
              <img
                id="preview-image"
                role="presentation"
                :alt="file.name"
                :src="file.objectURL"
                data-entity="pages-shareShareItElement-previewImage"
                data-test="pages-shareIt-previewImage"
              />
            </div>
          </div>
        </div>
      </template>
    </FileUpload>
    <ProgressBar
      v-if="showProgressBar"
      mode="indeterminate"
      class="share-it-element__progress-bar mb-6 mt-6"
      data-entity="pages-shareShareItElement-progressBar"
      data-test="pages-shareIt-progressBar"
      style="height: 6px"
    />
    <div
      v-if="fileUploaded"
      class="text-center share-it-element__text share-it-element__uploaded-text"
    >
      <TextElement :settings="{ content: settings.uploadedText }" />
      <ButtonElement
        class="share-it-element__btn share-it-element__upload-again-btn"
        @click="resetUpload"
        :settings="uploadAgainButtonSettings"
        data-entity="pages-shareShareItElement-upload-again"
        data-test="pages-shareIt-upload"
      />
    </div>
    <div class="flex items-center justify-center share-it-element__tos mt-6">
      <p class="text-center">
        By clicking to submit content, you agree to our
        <a
          class="share-it-element__tos-link"
          data-entity="pages-shareShareItElement-termsOfService"
          data-test="pages-shareIt-termsOfService"
          href="https://support.tagboard.com/knowledge-base/user-terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
        >Terms of Service</a>.
      </p>
    </div>
  </div>
</template>

<style lang="scss">
.share-it-element {
  &__btn {
    padding: 0 1rem;
  }

  &__tos {
    font-family: v-bind(tosFontFamily);
    color: v-bind(tosTextColor);
  }

  &__tos-link {
    color: v-bind(tosLinkColor);
  }

  &__content {
    background-color: rgba(255, 255, 255, .5);
    margin: 0 auto 1rem auto;
    width: 100%;
    max-width: 15rem;
    height: 15rem;

    @media screen and (min-width: 640px) {
      max-width: 25rem;
      height: 20rem;
    }
  }

  &__text-input {
    width: 100%;
    max-width: 15rem;
  }

  &__text-label {
    font-family: v-bind(tosFontFamily);
    color: black;
  }

  &__text-input:focus + &__text-label {
    color: v-bind(tosTextColor);

  }

  &__character-count {
    color: v-bind(tosTextColor);
  }

  &__text-disclaimer {
    font-family: v-bind(tosFontFamily);
    color: v-bind(tosTextColor);
    max-width: 15rem;
    font-size: 0.75rem;
    text-align: center;
    margin: 1rem 0;
  }

  .element {
    margin: 0;
  }
}

#preview-image {
  max-height: 30vh;
  object-fit: contain;
}

.share-it-element__required-text {
  font-family: v-bind(requiredTextFont);
  color: v-bind(requiredTextColor);
  font-size: v-bind(requiredTextSize);
}
</style>
